import { Component, Input, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BannerNotification, InAppNotificationService } from '@buspro/web/core';

/**
 * The announcement bar at the top of the workspace.
 *
 * It takes a `BannerNotification` view model rather than the generated
 * `AppNotificationDto` it used to take, so regenerating the API client cannot
 * change what this component is handed. It also renders the notification's title
 * — the old markup showed only `message`, while the useful context (route name)
 * sat unused in the fields beside it.
 */
@Component({
  selector: 'tenant-top-banner',
  templateUrl: './top-banner.component.html',
  styleUrls: ['./top-banner.component.css'],
  standalone: true,
})
export class TopBannerComponent {
  private router = inject(Router);
  private notificationService = inject(InAppNotificationService);

  @Input({ required: true }) notification!: BannerNotification;

  /** How many announcements are waiting behind this one. */
  readonly queuedCount = computed(() => this.notificationService.queuedCount());

  dismiss(): void {
    this.notificationService.dismiss();
  }

  goToAction(): void {
    const url = this.notification.ctaUrl;
    if (!url) {
      return;
    }

    // `navigateByUrl`, not `navigate([url])`: the server sends a whole URL and
    // this preserves any query string or fragment in it. External URLs are
    // refused — a banner must not be able to steer the app off-site.
    if (/^[a-z][a-z0-9+.-]*:|^\/\//i.test(url)) {
      return;
    }

    // Navigating is what satisfies the call to action, and the service clears the
    // banner when the user arrives — so there is no dismiss() call here.
    this.router.navigateByUrl(url);
  }
}
